import type { PartnerLevel } from './PartnerBehaviors';
import type { RelicDefinition } from './relicCatalog';
import { RELIC_RARITY_META } from './relicCatalog';
import type { PartnerType, ShopItemDefinition } from './shopCatalog';
import { getPartnerTags, type SynergyTag } from './SynergySystem';

export type BuildRoute = SynergyTag | '经济' | '风险' | '通用';

export interface BuildRouteMeta {
  label: BuildRoute;
  color: number;
  cssColor: string;
}

export interface PartnerDetailData {
  kind: 'partner';
  name: string;
  level: PartnerLevel;
  routes: BuildRouteMeta[];
  attackDescription: string;
  current: PartnerLevelStats;
  next?: PartnerLevelStats;
}

export interface PartnerLevelStats {
  level: PartnerLevel;
  damageMultiplier: number;
  attacksPerSecondMultiplier: number;
  rangeMultiplier: number;
}

export interface RelicDetailData {
  kind: 'relic';
  name: string;
  rarityLabel: string;
  rarityColor: number;
  routes: BuildRouteMeta[];
  description: string;
  stacks: number;
  diminishingFactor?: number;
  stackingText: string;
}

export const BUILD_ROUTE_META: Record<BuildRoute, BuildRouteMeta> = {
  射击: { label: '射击', color: 0x4d8dff, cssColor: '#4d8dff' },
  武斗: { label: '武斗', color: 0xffc857, cssColor: '#ffc857' },
  法术: { label: '法术', color: 0xb56cff, cssColor: '#b56cff' },
  支援: { label: '支援', color: 0x61d88f, cssColor: '#61d88f' },
  经济: { label: '经济', color: 0xf2c15c, cssColor: '#f2c15c' },
  风险: { label: '风险', color: 0xd95b4f, cssColor: '#d95b4f' },
  通用: { label: '通用', color: 0xc8c8c8, cssColor: '#c8c8c8' },
};

const PARTNER_LEVEL_STATS: Record<PartnerLevel, PartnerLevelStats> = {
  1: { level: 1, damageMultiplier: 1, attacksPerSecondMultiplier: 1, rangeMultiplier: 1 },
  2: { level: 2, damageMultiplier: 1.3, attacksPerSecondMultiplier: 1 / 0.87, rangeMultiplier: 1.1 },
  3: { level: 3, damageMultiplier: 1.7, attacksPerSecondMultiplier: 1 / 0.77, rangeMultiplier: 1.2 },
  4: { level: 4, damageMultiplier: 2.2, attacksPerSecondMultiplier: 1 / 0.67, rangeMultiplier: 1.35 },
  5: { level: 5, damageMultiplier: 3, attacksPerSecondMultiplier: 1 / 0.59, rangeMultiplier: 1.5 },
};

const ECONOMY_TAGS = new Set(['经济', '材料', '拾取', '买卖', '刷新', '存钱', '前期', '无限']);
const RISK_TAGS = new Set(['风险', '随机', '商店', '少伙伴', '持续', '连杀']);
const GENERIC_TAGS = new Set([
  '伤害',
  '攻速',
  '固定伤害',
  '百分比伤害',
  '暴击',
  '生存',
  '生命',
  '续航',
  '减伤',
  '护盾',
  '反伤',
  '机动',
  '拾取',
  '全能',
  '击杀',
]);

export function getPartnerBuildRoutes(type: PartnerType): BuildRouteMeta[] {
  return getPartnerTags(type).map((tag) => BUILD_ROUTE_META[tag]);
}

export function getRelicBuildRoutes(relic: RelicDefinition): BuildRouteMeta[] {
  if (relic.tag === '射击' || relic.tag === '弹道') return [BUILD_ROUTE_META.射击];
  if (relic.tag === '武斗') return [BUILD_ROUTE_META.武斗];
  if (relic.tag === '法术') return [BUILD_ROUTE_META.法术];
  if (relic.tag === '支援') return [BUILD_ROUTE_META.支援];
  if (ECONOMY_TAGS.has(relic.tag)) return [BUILD_ROUTE_META.经济];
  if (RISK_TAGS.has(relic.tag)) return [BUILD_ROUTE_META.风险];
  if (GENERIC_TAGS.has(relic.tag)) return [BUILD_ROUTE_META.通用];
  throw new Error(`Unknown relic build route tag: ${relic.tag}`);
}

export function getShopItemBuildRoutes(item: ShopItemDefinition | RelicDefinition): BuildRouteMeta[] {
  if (item.kind === 'partner') {
    return getPartnerBuildRoutes(item.partnerType);
  }
  return getRelicBuildRoutes(item);
}

export function formatBuildRoutes(routes: readonly BuildRouteMeta[]) {
  return routes.map((route) => route.label).join(' · ');
}

export function getPartnerDetailData(item: ShopItemDefinition, level: PartnerLevel): PartnerDetailData {
  const current = PARTNER_LEVEL_STATS[level];
  const next = level < 5 ? PARTNER_LEVEL_STATS[(level + 1) as PartnerLevel] : undefined;
  return {
    kind: 'partner',
    name: item.name,
    level,
    routes: getPartnerBuildRoutes(item.partnerType),
    attackDescription: item.description,
    current,
    next,
  };
}

export function getRelicDetailData(relic: RelicDefinition, stacks: number): RelicDetailData {
  const rarity = RELIC_RARITY_META[relic.rarity];
  const safeStacks = Math.max(0, stacks);
  return {
    kind: 'relic',
    name: relic.name,
    rarityLabel: rarity.label,
    rarityColor: rarity.color,
    routes: getRelicBuildRoutes(relic),
    description: relic.description,
    stacks: safeStacks,
    diminishingFactor: relic.diminishingFactor,
    stackingText: relic.diminishingFactor === undefined
      ? '线性叠加'
      : `第1层全效，后续 ×${relic.diminishingFactor}`,
  };
}
